<script setup>
import { ref, watch, inject, computed, nextTick } from 'vue';
import { useDisplay } from 'vuetify';
import useDialog from '@/composables/use-dialog';
import useCustomForm from '@/composables/use-CustomForm';
import CreationDialog from '@/components/widgets/creationDialog/CreationDialog.vue';
import poboxFormTemplate from '@/config/templates/poboxDialog';
import FormSectionTitle from '@/components/widgets/forms/FormSectionTitle.vue';
import FormLine from '@/components/widgets/creationDialog/FormLine.vue';
import DefaultCustomer from '@/components/Pobox/DefaultCustomers.vue';
import PasswordDialog from '@/components/Pobox/PasswordDialog.vue';
import useLoaderStore from '@/stores/loader';
import PoboxDetails from '@/components/Pobox/PoboxDetails.vue';
import CreateCustomerDialog from '@/components/Customer/ShipperForm.vue';
import TabPanel from '@/components/widgets/TabPanel.vue';
import { useUserStore } from '@/stores/user';

const { show } = useDialog();
const backendApi = inject('backendApi');
const user = useUserStore();
defineProps(['title']);
const emit = defineEmits(['saved', 'close']);
const userAgency = {
    id: user.data?.agency?.agencyid,
    ...user.data?.agency
};

const { fieldModels, onValidatedSubmit, clearForm, updatedFromResponse } =
    useCustomForm(poboxFormTemplate, {
        agency: userAgency
    });

//  Visibility of the section of the form
const sectionVisibilities = ref(poboxFormTemplate.map(() => true));

const poboxId = ref(null);
const loading = ref(false);
const pobox = ref({});
const displayErrorMessage = ref(false);
const { mdAndDown } = useDisplay();
const backendErrorMessage = ref(null);
const validValue = (val) => (val && val !== 'null' ? val : '');
const lastActionTriggered = ref(null);
const createCustomerDialogRef = ref(null);
const changePasswordDialogRef = ref(null);
const customerId = ref(null);
const customerData = ref(null);
const loaderStore = useLoaderStore();
const defaultCustomerRef = ref(null);
const mode = computed(() => (pobox.value ? 'view' : 'edit'));

watch(show, (value) => {
    clearForm();
    if (!value) {
        pobox.value = null;
        displayErrorMessage.value = false;
        backendErrorMessage.value = null;
    }
});

const tabs = [
    {
        label: 'Details',
        icon: 'mdi-information-outline',
        slotName: 'status'
    }
];

const actions = computed(() => {
    // Otherwise, return all actions
    return [
        {
            title: 'UPDATE CLIENT',
            icon: 'mdi-account-box-edit-outline',
            name: 'updateCustomer'
        },
        {
            title: 'UPDATE PASSWORD',
            icon: 'mdi-form-textbox-password',
            name: 'updateUser'
        }
    ];
});

const createWithExistingCustomer = [
    {
        title: 'SEARCH CLIENT',
        icon: 'mdi-account-search-outline',
        name: 'searchClient'
    }
];

const handleAction = (action) => {
    switch (action) {
        case 'searchClient':
            onSelectCustomerPressed();
            break;
        case 'updateCustomer':
            openCustomerDialog();
            break;
        case 'updateUser':
            onChangePassword(pobox.value.user.id);
            break;
    }
};

const onPrebuiltItemsSelected = (item) => {
    customerId.value = item?.id ?? null;
    customerData.value = item;
    fieldModels['customer.number'].value.value = item.number;
    fieldModels['name'].value.value = item.name;
    fieldModels['lastname'].value.value = item.lastname;
    fieldModels['docid'].value.value = item.adrdefault?.docid;
    fieldModels['email'].value.value = item.email;
    fieldModels['phone'].value.value = item.adrdefault?.phone;
    fieldModels['mobile'].value.value = item.adrdefault?.mobile;
    fieldModels['agency'].value.value = item.agency;
    fieldModels['groupTariff'].value.value = item.groupTariff;
    fieldModels['location'].value.value = item.location;
    fieldModels['address'].value.value = item.adrdefault?.address;
    fieldModels['city'].value.value = item.adrdefault?.city;
    fieldModels['zip'].value.value = item.adrdefault?.zip;
    fieldModels['customerType'].value.value =
        item.customerType?.id === 2 ? true : false;
};

const isFieldDisabled = (field) => {
    const customerFields = [
        'name',
        'lastname',
        'docid',
        'email',
        'phone',
        'mobile',
        'agency',
        'agent',
        'groupTariff',
        'location',
        'address',
        'city',
        'zip',
        'customerType',
        'username',
        'password',
        'passwordRepeat'
    ];

    if (customerId.value !== null && customerFields.includes(field.model)) {
        return true;
    }

    if (field.model === 'customer.number') {
        return true;
    }

    return false;
};

const requestCreate = (options) => {
    backendApi()
        .poBox()
        .create({
            agency: options.agency?.id,
            name: options.name,
            lastName: options.lastname,
            docid: options.docid,
            phone: options.phone,
            mobile: options.mobile,
            email: options.email,
            agent: options.agent?.id,
            location: options.location?.id,
            customerType: options.customerType === true ? 2 : 1,
            poboxType: 'NORMAL',
            groupTariff: options.groupTariff?.id,
            address: options.address,
            city: options.city?.id,
            zip: options.zip,
            username: options.username,
            password: options.password
        })
        .then((response) => {
            if (response) {
                displayErrorMessage.value = false;
                backendErrorMessage.value = null;
                customerId.value = null;
                close();
                emit('saved');
            }
        })
        .catch((error) => {
            displayErrorMessage.value = true;
            backendErrorMessage.value = error.message;
        })
        .finally(() => {
            loading.value = false;
            poboxId.value = null;
        });
};

const requestCreateWithCustomer = (options) => {
    backendApi()
        .poBox()
        .createExisting({
            customerId: customerId.value,
            poboxType: 'NORMAL',
            username: options.username,
            password: options.password
        })
        .then(() => {
            displayErrorMessage.value = false;
            backendErrorMessage.value = null;
            customerId.value = null;
            close();
            emit('saved');
        })
        .catch((error) => {
            console.error(error);
            displayErrorMessage.value = true;
            backendErrorMessage.value = error.message;
        })
        .finally(() => {
            loading.value = false;
            poboxId.value = null;
        });
};

const open = async (data) => {
    backendErrorMessage.value = null;

    if (data) {
        updatedFromResponse(data);
        pobox.value = data;
        const { customer, agency, agent, user } = data;
        const adr = customer.adrdefault;
        const city = adr.city;
        const state = city.state;
        const country = state.country;
        customerId.value = customer.id;
        customerData.value = customer;
        nextTick(() => {
            loaderStore.showLoader();
            fieldModels['customer.number'].value.value = validValue(
                customer.number
            );

            fieldModels['name'].value.value = validValue(customer.name);
            fieldModels['lastname'].value.value = validValue(customer.lastname);
            fieldModels['docid'].value.value = validValue(adr.docid);
            fieldModels['email'].value.value = validValue(customer.email);
            fieldModels['phone'].value.value = validValue(adr.phone);
            fieldModels['mobile'].value.value = validValue(adr.mobile);

            fieldModels['agency'].value.value = validValue(agency?.name);
            fieldModels['agent'].value.value = validValue(agent?.name);
            fieldModels['groupTariff'].value.value = validValue(
                customer.groupTariff
            );
            fieldModels['location'].value.value = validValue(
                customer.location?.code
            );

            fieldModels['address'].value.value = validValue(adr.address);
            fieldModels['city'].value.value = validValue(city.name);
            fieldModels['state'].value.value = validValue(state.name);
            fieldModels['country'].value.value = validValue(country.name);
            fieldModels['zip'].value.value = validValue(adr.zip);

            fieldModels['username'].value.value = validValue(user.username);
            fieldModels['password'].value.value = 'xx';
            fieldModels['passwordRepeat'].value.value = 'xx';
            fieldModels['customerType'].value.value =
                customer.customerType?.id === 2 ? true : false;
            loaderStore.hideLoader();
        });
    } else {
        lastActionTriggered.value = 'create';
        customerId.value = null;
        pobox.value = null;
        clearForm();
    }
    show.value = true;
};

const close = () => {
    customerId.value = null;
    lastActionTriggered.value = null;
    poboxId.value = null;
    pobox.value = null;
    clearForm();
    show.value = false;
    emit('close');
};

const onSelectCustomerPressed = () => {
    defaultCustomerRef.value.open();
};

const onChangePassword = (id = null) => {
    changePasswordDialogRef.value.open(id);
};

function openCustomerDialog() {
    const params = {};
    if (user.data?.user?.type === 'POBOX' && pobox.value?.agency) {
        params.agency = pobox.value.agency;
    }
    createCustomerDialogRef.value.open(customerData.value, params);
}

const onSaveBtnPressed = () => {
    onValidatedSubmit((options) => {
        loading.value = true;
        if (customerId.value) {
            lastActionTriggered.value = 'createWithCustomer';
            requestCreateWithCustomer(options);
        } else {
            lastActionTriggered.value = 'create';
            requestCreate(options);
        }
    });
};

defineExpose({
    open,
    close
});
</script>

<template>
    <CreationDialog
        v-model="show"
        :page="title ?? $t('Pobox')"
        :title="pobox ? pobox.number : 'Create'"
        :compact-header-margin="true"
        :hideEditAction="true"
        :actions="pobox ? actions : createWithExistingCustomer"
        :hideNewAction="true"
        :hideRemoveAction="true"
        :has-attachments="pobox ? true : false"
        :show-toggle-sidebar-button="pobox ? !mdAndDown : false"
        :saving="loading"
        :mode="mode"
        :error-message="backendErrorMessage"
        @close="close"
        @action="handleAction"
        @save="onSaveBtnPressed"
    >
        <template v-slot:form>
            <section
                v-for="(section, index) in poboxFormTemplate.filter(
                    (s) => mode !== 'view' || s.title !== 'Password'
                )"
                :key="index"
            >
                <FormSectionTitle
                    :title="section.title"
                    v-model="sectionVisibilities[index]"
                />
                <transition name="slide">
                    <v-row v-show="sectionVisibilities[index]" class="mt-3">
                        <FormLine
                            v-for="field in section.fields.filter((f) => {
                                if (mode === 'view') {
                                    return (
                                        f.model !== 'password' &&
                                        f.model !== 'passwordRepeat'
                                    );
                                } else {
                                    return (
                                        f.model !== 'state' &&
                                        f.model !== 'country' &&
                                        f.model !== 'customer.number'
                                    );
                                }
                            })"
                            :key="field.label"
                            :fullWidth="field.fullWidth"
                            :oneColumn="field.oneColumn"
                            :label="field.label"
                            :mode="mode"
                            :component="field.component"
                            :fieldModels="fieldModels"
                            :error-messages="
                                fieldModels[field.model].errorMessage.value
                            "
                            v-model="fieldModels[field.model].value.value"
                            :required="field.required"
                            :model-name="field.model"
                            :align="field.align"
                            :editable="isFieldDisabled(field)"
                            :return-object="field.returnObject"
                            @onChange="onFiledChanged"
                            @update:modelValue="onUpdateFieldValue"
                            :args="{
                                agency: fieldModels['agency']?.value?.value
                            }"
                        />
                    </v-row>
                </transition>
            </section>
        </template>

        <template v-slot:attachments>
            <TabPanel :tabs="tabs" :show-content="true" :elevation="0">
                <template v-slot:status>
                    <PoboxDetails
                        v-if="pobox != null && pobox.id > 0"
                        :detailed-item="pobox"
                    ></PoboxDetails>
                </template>
            </TabPanel>
        </template>
    </CreationDialog>

    <CreateCustomerDialog
        ref="createCustomerDialogRef"
        @update="onPrebuiltItemsSelected"
    ></CreateCustomerDialog>

    <PasswordDialog ref="changePasswordDialogRef"></PasswordDialog>

    <DefaultCustomer
        ref="defaultCustomerRef"
        @selected="onPrebuiltItemsSelected"
    ></DefaultCustomer>
</template>

<style lang="scss" scoped>
section {
    margin-bottom: 3rem;
}
</style>
